import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components_dashboard/ui/button';
import { Input } from '../components_dashboard/ui/input';
import { Label } from '../components_dashboard/ui/label';
import { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator } from '../components_dashboard/ui/input-otp';
import { Phone, ArrowLeft, Loader2, CheckCircle } from 'lucide-react';
import { getDefaultAuthenticatedRoute } from '../lib/userAccess';

export default function LoginOTP() {
  const [step, setStep] = useState('phone'); // 'phone' or 'otp'
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const { sendOtp, verifyOtp } = useAuth();
  const navigate = useNavigate();

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await sendOtp(phone);
      setOtpSent(true);
      setStep('otp');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const user = await verifyOtp(phone, otp);
      navigate(getDefaultAuthenticatedRoute(user));
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen p-4 login-bg">
      <div className="w-full max-w-md">
        <div className="p-8 glass-modal rounded-2xl shadow-float animate-slide-up">
          {/* Logo */}
          <div className="mb-8 text-center">
            <a href={process.env.REACT_APP_LANDING_BASE_URL || "/"} className="inline-flex items-center gap-2 mb-2 hover:opacity-80 transition-opacity">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-accent">
                <span className="text-lg font-bold text-white">T</span>
              </div>
              <span className="text-2xl font-bold tracking-tight text-slate-900">TechLadder.ai</span>
            </a>
            <p className="text-sm text-slate-500">Login with your phone number</p>
          </div>

          {step === 'phone' ? (
            <form onSubmit={handleSendOtp} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="phone" className="font-medium text-slate-700">Phone Number</Label>
                <div className="relative">
                  <Phone className="absolute w-4 h-4 -translate-y-1/2 left-3 top-1/2 text-slate-400" />
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+1 (555) 000-0000"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="pl-10 h-11 border-slate-200 focus:border-accent focus:ring-accent"
                    required
                    data-testid="phone-input"
                  />
                </div>
              </div>

              {error && (
                <div className="p-3 text-sm text-red-600 border border-red-100 rounded-lg bg-red-50" data-testid="otp-error">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                className="w-full font-medium text-white rounded-lg h-11 bg-primary hover:bg-primary-700 shadow-soft"
                disabled={loading}
                data-testid="send-otp-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending OTP...
                  </>
                ) : (
                  'Send OTP'
                )}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp} className="space-y-5">
              {/* OTP Sent Success */}
              {otpSent && (
                <div className="flex items-center gap-2 p-3 text-sm text-green-700 border border-green-100 rounded-lg bg-green-50">
                  <CheckCircle className="w-4 h-4" />
                  OTP sent to {phone}
                </div>
              )}

              <div className="space-y-2">
                <Label className="font-medium text-slate-700">Enter OTP</Label>
                <p className="mb-3 text-xs text-slate-500">Enter the 6-digit code sent to your phone</p>
                <div className="flex justify-center">
                  <InputOTP
                    maxLength={6}
                    value={otp}
                    onChange={(value) => setOtp(value)}
                    data-testid="otp-input"
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                    </InputOTPGroup>
                    <InputOTPSeparator />
                    <InputOTPGroup>
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <p className="mt-2 text-xs text-center text-slate-400">
                  Demo OTP: <span className="font-mono font-medium text-accent">123456</span>
                </p>
              </div>

              {error && (
                <div className="p-3 text-sm text-red-600 border border-red-100 rounded-lg bg-red-50" data-testid="otp-verify-error">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                className="w-full font-medium text-white rounded-lg h-11 bg-primary hover:bg-primary-700 shadow-soft"
                disabled={loading || otp.length !== 6}
                data-testid="verify-otp-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify & Login'
                )}
              </Button>

              <Button
                type="button"
                variant="ghost"
                onClick={() => { setStep('phone'); setOtp(''); setError(''); }}
                className="w-full text-slate-500 hover:text-slate-700"
                data-testid="change-phone-button"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Change phone number
              </Button>
            </form>
          )}

          {/* Back to email login */}
          <p className="mt-6 text-sm text-center text-slate-500">
            <Link to="/login" className="inline-flex items-center gap-1 font-medium text-accent hover:underline" data-testid="back-to-email-link">
              <ArrowLeft className="w-3 h-3" />
              Back to email login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
